/**
 * Профіль — хаб особистих налаштувань (Inflow 2.0).
 *
 * Секції:
 *   1. Bio (вільний текст для AI, 300-2000 символів)
 *   2. Сфери вакансій (категорії — toggle)
 *   3. Джерела вакансій (4 toggle: TG-канали / чати / Threads / Freelancehunt)
 *   4. Додати свої групи (stub — поки заглушка)
 *
 * Стиль: Home.tsx editorial — bg-bg, font-display, scribble decoration,
 * мінімум emoji.
 */

import { CornerBadge } from "@/components/CornerBadge";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useCategories,
  useProfileFull,
  useSubmitUserGroups,
  useUpdateProfileFull,
} from "@/api/hooks";
import type {
  ProfileFullUpdateIn,
  SourcesState,
  WorkFormat,
} from "@/api/hooks";
import { Link } from "react-router-dom";
import { BackNav } from "@/components/BackNav";
import { SpheresPicker } from "@/components/SpheresPicker";
import { AIPresetsManager } from "@/components/AIPresetsManager";
import { Collapse } from "@/components/Collapse";
import { Icon, ScribbleUnderline, type IconName } from "@/components/Icon";
import { useMySubscription } from "@/api/hooks";
import { isActiveSubscription } from "@/api/types";
import { CenteredError, Spinner } from "@/components/Spinner";
import { isLikelyUrl } from "@/lib/utils";
import { tg } from "@/lib/telegram";
import { showAppAlert } from "@/lib/appAlert";
import { BirthdayModal } from "@/components/BirthdayModal";
import { VoiceBioButton } from "@/components/VoiceBioButton";

const SOURCE_META: Array<{
  key: keyof SourcesState;
  title: string;
  description: string;
}> = [
  {
    key: "telegram",
    title: "Telegram-канали",
    description: "Тематичні канали з вакансіями",
  },
  {
    key: "chats",
    title: "Telegram-чати",
    description: "Активні чати-комʼюніті, де клієнти шукають фахівців",
  },
  {
    key: "threads",
    title: "Threads",
    description: "Свіжі дописи з пошуком послуг",
  },
  {
    key: "fhunt",
    title: "Freelancehunt",
    description: "Українська freelance-біржа",
  },
  {
    key: "finder",
    title: "inFLOW Finder",
    description: "Ексклюзивні вакансії від inFLOW",
  },
];

// Формати роботи (owner 2026-07-23). «unspecified» — заблокований-ВКЛ (locked).
//: Мови вакансій (owner 2026-09-19). Замір по проду за 30 днів (1500 вакансій):
//: українською 79%, російською 21%, англійською <1%. Саме ці числа стоять у
//: підказці — людина має розуміти, що втрачає.
const LANG_META: Array<{ key: string; title: string; description: string }> = [
  {
    key: "uk",
    title: "Українська",
    description: "Близько 8 з 10 вакансій",
  },
  {
    key: "ru",
    title: "Російська",
    description: "Приблизно кожна п'ята вакансія",
  },
  {
    key: "en",
    title: "Англійська",
    description: "Рідко — менше 1% вакансій",
  },
];

const WORK_KIND_META: Array<{ key: string; title: string; description: string }> = [
  {
    key: "job",
    title: "Вакансії",
    // Правило власника 25.09: агенція, зарплата, офіс чи графік. Обидва описи
    // — у два рядки приблизно однакової довжини (owner 25.09).
    description: "Найчастіше повний день у компанії чи агенції, із зарплатою й графіком",
  },
  {
    key: "order",
    title: "Замовлення",
    description: "Робота над конкретним проєктом чи задачею, оплата за виконаний обсяг",
  },
];

const WORK_FORMAT_META: Array<{
  key: WorkFormat | "unspecified";
  title: string;
  description: string;
  locked?: boolean;
}> = [
  { key: "online", title: "Онлайн", description: "Віддалена робота / remote" },
  {
    key: "offline",
    title: "Офлайн",
    description: "Офіс / на місці / конкретне місто",
  },
  { key: "hybrid", title: "Гібрид", description: "І офіс, і віддалено" },
  {
    key: "unspecified",
    title: "Не вказано",
    description: "Формат не вказано у вакансії",
    locked: true,
  },
];

// Формат бот-сповіщень про вакансії (юзер 2026-06-12). Кожен варіант має
// міні-ілюстрацію у вигляді Telegram-повідомлення — щоб юзер бачив, як саме
// прийде вакансія. Тексти-приклади 1:1 з реальним build_paid_vacancy_message:
// «оригінал» = повний текст із джерела, «стисло» = AI-переказ.
const VACANCY_FORMAT_OPTIONS: Array<{
  value: "original" | "summary";
  label: string;
  sublabel: string;
  preview: string;
}> = [
  {
    value: "original",
    label: "Оригінал",
    sublabel: "Повний текст вакансії — точно як у джерелі",
    preview:
      "📣 Шукаємо SMM-менеджера в digital-агенцію 🚀\n\n" +
      "Потрібна людина на ведення Instagram і TikTok: контент-план, " +
      "сторіс, reels, комунікація з клієнтами.\n\n" +
      "Що пропонуємо:\n" +
      "• ставка 25 000 грн + бонуси\n" +
      "• віддалено, гнучкий графік\n" +
      "• команда дизайнерів і таргетологів\n\n" +
      "Пиши в особисті 👇",
  },
  {
    value: "summary",
    label: "Стисло (AI-переказ)",
    sublabel: "Короткий зміст вакансії — суть за кілька секунд",
    preview:
      "📣 Шукають SMM-менеджера в digital-агенцію на ведення Instagram " +
      "і TikTok. Віддалено, ставка 25 000 грн + бонуси, гнучкий графік.",
  },
];

// День народження (owner 2026-07-23): "YYYY-MM-DD" → "15 березня 1995".
const UA_MONTHS = [
  "січня", "лютого", "березня", "квітня", "травня", "червня",
  "липня", "серпня", "вересня", "жовтня", "листопада", "грудня",
];
function formatBirthday(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${UA_MONTHS[m - 1] ?? ""} ${y}`.trim();
}

export function ProfilePage() {
  const profileQuery = useProfileFull();
  const categoriesQuery = useCategories();
  const updateMutation = useUpdateProfileFull();
  const submitGroupsMutation = useSubmitUserGroups();
  // Юзер 2026-06-06: fhunt/finder — premium, FREE бачить замочок.
  const profileSubQuery = useMySubscription();
  const profileIsPaid =
    !!profileSubQuery.data &&
    isActiveSubscription(profileSubQuery.data) &&
    profileSubQuery.data.status === "active";

  const [bio, setBio] = useState<string>("");
  const [portfolioUrl, setPortfolioUrl] = useState<string>("");
  const [selectedCats, setSelectedCats] = useState<Set<number>>(new Set());
  const [sources, setSources] = useState<SourcesState>({
    telegram: true,
    chats: true,
    threads: true,
    fhunt: false,
    finder: false,
  });
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [groupsText, setGroupsText] = useState("");
  // День народження (owner 2026-07-23) — set-once. Попап відкриваємо лише коли
  // ще не вказано; збереження одразу через updateMutation.
  const [bdayModalOpen, setBdayModalOpen] = useState(false);

  // Чорний список слів (owner 2026-09-06): вакансії з цими словами не
  // приходять. Зберігаємо по Enter або по кнопці — не на кожен символ.
  const blockedWords = profileQuery.data?.blocked_words ?? [];
  const [wordDraft, setWordDraft] = useState("");
  const saveWords = (words: string[]) =>
    updateMutation.mutate(
      { blocked_words: words },
      { onError: (e) => showAppAlert((e as Error).message) },
    );
  const addWord = () => {
    const w = wordDraft.trim().toLowerCase();
    if (!w) return;
    if (blockedWords.includes(w)) {
      setWordDraft("");
      return;
    }
    if (blockedWords.length >= 30) {
      showAppAlert("Більше тридцяти слів не збережемо.");
      return;
    }
    saveWords([...blockedWords, w]);
    setWordDraft("");
  };
  const birthday = profileQuery.data?.birthday ?? null;
  // Змінити вже вказану дату можна один раз (owner 2026-09-07; 25.09 власник
  // знайшов, що міняти можна без кінця). Далі — через підтримку.
  const bdayChangeLeft = profileQuery.data?.birthday_change_left ?? true;
  const bdayLocked = !!birthday && !bdayChangeLeft;
  const handleSaveBirthday = (isoDate: string) => {
    updateMutation.mutate(
      { birthday: isoDate },
      {
        onSuccess: () => {
          setBdayModalOpen(false);
          tg()?.HapticFeedback?.notificationOccurred?.("success");
        },
        onError: (e) => showAppAlert((e as Error).message),
      },
    );
  };

  // Юзер 2026-05-24: «згорни блоки в Profile щоб не займали стільки місця».
  // Accordion state для 3 секцій (Bio / Сфери / Джерела). За замовчуванням
  // ВСІ згорнуті — юзер бачить компактний overview і розгортає що треба.
  // «Формат вакансій» секцію видалили (юзер 2026-05-24: «хуй з ним»).
  const [bioSectionOpen, setBioSectionOpen] = useState(false);
  const [catsSectionOpen, setCatsSectionOpen] = useState(false);
  const [sourcesSectionOpen, setSourcesSectionOpen] = useState(false);
  const [formatSectionOpen, setFormatSectionOpen] = useState(false);
  // Формат вакансій у боті (юзер 2026-06-12) — draft для миттєвого UI.
  const [vacFormatDraft, setVacFormatDraft] = useState<
    "original" | "summary" | null
  >(null);
  const vacancyFormat =
    vacFormatDraft ?? profileQuery.data?.vacancy_format ?? "original";
  const setVacancyFormat = (fmt: "original" | "summary") => {
    if (fmt === vacancyFormat) return;
    tg()?.HapticFeedback?.selectionChanged?.();
    setVacFormatDraft(fmt);
    updateMutation.mutate(
      { vacancy_format: fmt },
      { onSettled: () => setVacFormatDraft(null) },
    );
  };

  // Формати роботи (owner 2026-07-23) — draft для миттєвого UI. «unspecified»
  // завжди ввімкнено (тумблер заблокований), у списку draft НЕ зберігається.
  const [workFmtSectionOpen, setWorkFmtSectionOpen] = useState(false);
  const [aiSectionOpen, setAiSectionOpen] = useState(false);
  // Для генерації потрібне лише «Про тебе» (owner 2026-09-15: посилання на
  // портфоліо необов'язкове, але якщо є — AI його використає).
  const aiProfileReady = (profileQuery.data?.bio?.trim().length ?? 0) > 50;
  const [langSectionOpen, setLangSectionOpen] = useState(false);
  const [langsDraft, setLangsDraft] = useState<string[] | null>(null);
  const langs: string[] =
    langsDraft ?? profileQuery.data?.langs ?? ["uk", "ru", "en"];
  const toggleLang = (code: string) => {
    const next = langs.includes(code)
      ? langs.filter((x) => x !== code)
      : [...langs, code];
    // Вимкнути ВСІ мови не можна: людина лишилась би взагалі без вакансій.
    if (next.length === 0) return;
    tg()?.HapticFeedback?.selectionChanged?.();
    setLangsDraft(next);
    updateMutation.mutate(
      { langs: next } as never,
      { onSettled: () => setLangsDraft(null) },
    );
  };

  const [workKindSectionOpen, setWorkKindSectionOpen] = useState(false);
  const [workKindsDraft, setWorkKindsDraft] = useState<string[] | null>(null);
  const workKinds: string[] =
    workKindsDraft ?? profileQuery.data?.work_kinds ?? ["job", "order"];
  const toggleWorkKind = (key: string) => {
    const next = workKinds.includes(key)
      ? workKinds.filter((x) => x !== key)
      : [...workKinds, key];
    // Вимкнути обидва типи не можна — людина лишилась би без вакансій.
    if (next.length === 0) return;
    tg()?.HapticFeedback?.selectionChanged?.();
    setWorkKindsDraft(next);
    updateMutation.mutate(
      { work_kinds: next } as never,
      { onSettled: () => setWorkKindsDraft(null) },
    );
  };

  const [workFmtDraft, setWorkFmtDraft] = useState<WorkFormat[] | null>(null);
  const workFormats: WorkFormat[] =
    workFmtDraft ?? profileQuery.data?.work_formats ?? ["online", "offline", "hybrid"];
  const toggleWorkFormat = (f: WorkFormat) => {
    const next = workFormats.includes(f)
      ? workFormats.filter((x) => x !== f)
      : [...workFormats, f];
    tg()?.HapticFeedback?.selectionChanged?.();
    setWorkFmtDraft(next);
    updateMutation.mutate(
      { work_formats: next },
      { onSettled: () => setWorkFmtDraft(null) },
    );
  };

  // Ініціалізуємо локальний стейт із сервера ОДИН раз (init-once). Якщо
  // синкати на кожну зміну profileQuery.data — автозбереження ↔ refetch
  // затиратимуть свіжі правки юзера. Після ініту локальний стейт = джерело
  // правди, автосейв тримає сервер у синхроні.
  const initedRef = useRef(false);
  useEffect(() => {
    if (!profileQuery.data || initedRef.current) return;
    initedRef.current = true;
    setBio(profileQuery.data.bio ?? "");
    setPortfolioUrl(profileQuery.data.portfolio_url ?? "");
    setSelectedCats(new Set(profileQuery.data.selected_category_ids));
    setSources(profileQuery.data.sources);
  }, [profileQuery.data]);

  const minChars = profileQuery.data?.bio_min_chars ?? 300;
  const maxChars = profileQuery.data?.bio_max_chars ?? 2000;
  const bioLen = bio.trim().length;
  const bioValid = bioLen === 0 || (bioLen >= minChars && bioLen <= maxChars);
  const initialBio = profileQuery.data?.bio ?? "";
  const initialCats = useMemo(
    () => new Set(profileQuery.data?.selected_category_ids ?? []),
    [profileQuery.data],
  );
  const initialSources = profileQuery.data?.sources;

  // Автозбереження (юзер 2026-06-11: «люди не розуміють що треба тиснути
  // Зберегти»). Дебаунс 700мс після останньої правки. bio шлемо ЛИШЕ
  // валідне (порожнє/недобір — не чіпаємо, як стара handleSave); сфери/
  // джерела — завжди (ідемпотентно). Фаєримо тільки коли реально є що
  // зберегти, щоб не слати порожні PUT.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<ProfileFullUpdateIn | null>(null);
  useEffect(() => {
    if (!profileQuery.data) return;
    const bt = bio.trim();
    // Порожнє bio теж зберігаємо (явне очищення — юзер 2026-06-14: «не можу
    // прибрати інфо про себе, воно повертається»). Інакше — 300-2000.
    const bioSaveable =
      bio !== initialBio &&
      (bt.length === 0 || (bt.length >= minChars && bt.length <= maxChars));
    const catsChanged =
      selectedCats.size !== initialCats.size ||
      [...selectedCats].some((id) => !initialCats.has(id));
    const sourcesChanged =
      !!initialSources &&
      (sources.telegram !== initialSources.telegram ||
        sources.chats !== initialSources.chats ||
        sources.threads !== initialSources.threads ||
        sources.fhunt !== initialSources.fhunt ||
        sources.finder !== initialSources.finder);
    const initialPortfolio = profileQuery.data?.portfolio_url ?? "";
    const pu = portfolioUrl.trim();
    // Зберігаємо портфоліо лише якщо змінилось І (порожнє=стерти АБО валідне).
    // Невалідне (рандомний текст) НЕ автозберігаємо — щоб бек не повертав 400.
    const portfolioSaveable =
      pu !== initialPortfolio.trim() && (pu === "" || isLikelyUrl(pu));
    if (!bioSaveable && !catsChanged && !sourcesChanged && !portfolioSaveable) {
      pendingSaveRef.current = null;
      return;
    }
    const payload: ProfileFullUpdateIn = {
      selected_category_ids: [...selectedCats],
      sources,
    };
    if (bioSaveable) payload.bio = bt;
    if (portfolioSaveable) payload.portfolio_url = pu;
    pendingSaveRef.current = payload;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const p = pendingSaveRef.current;
      pendingSaveRef.current = null;
      if (p) updateMutation.mutate(p);
    }, 700);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bio, portfolioUrl, selectedCats, sources, profileQuery.data]);

  // Flush незбереженого автосейву при виході зі сторінки (юзер 2026-06-14:
  // очистив bio й одразу натиснув «Меню» → дебаунс 700мс відмінявся
  // розмонтуванням, зміна не зберігалась).
  useEffect(() => {
    return () => {
      if (pendingSaveRef.current) {
        updateMutation.mutate(pendingSaveRef.current);
        pendingSaveRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (profileQuery.isLoading || categoriesQuery.isLoading) return <Spinner />;
  if (profileQuery.isError)
    return <CenteredError message={profileQuery.error.message} />;
  if (categoriesQuery.isError)
    return <CenteredError message={categoriesQuery.error.message} />;

  const categories = categoriesQuery.data!.items;

  const toggleCat = (id: number) => {
    tg()?.HapticFeedback?.selectionChanged?.();
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSource = (key: keyof SourcesState) => {
    tg()?.HapticFeedback?.selectionChanged?.();
    setSources((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmitGroups = async () => {
    const t = groupsText.trim();
    if (t.length < 3) {
      showAppAlert("Додай посилання на групи у поле нижче.");
      return;
    }
    try {
      await submitGroupsMutation.mutateAsync(t);
      tg()?.HapticFeedback?.notificationOccurred?.("success");
      showAppAlert(
        "Посилання прийняті до перевірки ✅. Ми додамо групи якщо їх ще немає в нас.",
      );
      setGroupsText("");
      setGroupsOpen(false);
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert(`Не вдалось надіслати: ${(e as Error).message}`);
    }
  };

  return (
    <div className="pb-8 overflow-x-hidden w-full max-w-full">
      {/* Top-level nav: pill «Меню» (BackNav default) — узгоджено з усіма
          іншими сторінками (Categories, KB, тощо). Лівий край рівно під
          лівим краєм всіх Hero/секцій (px-5). */}
      <div className="px-5 pt-5">
        <BackNav />
      </div>

      {/* Hero — eyebrow 32px від логотипа (BackNav mb-4 + pt-4), як скрізь */}
      <div className="relative px-5 pt-4 pb-2">
        <div className="flex items-center gap-2 font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-ink-dim">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          профіль
        </div>
        <h1
          className="mt-3 font-display font-bold text-ink break-words"
          style={{ fontSize: "2.125rem", lineHeight: 1.02, letterSpacing: "-0.025em" }}
        >
          Налаштування
          <br />
          <span className="relative inline-block">
            <span className="accent-italic" style={{ fontWeight: 600 }}>
              для тебе.
            </span>
            <ScribbleUnderline width={130} className="absolute -bottom-2 -left-1" />
          </span>
        </h1>
      </div>

      {/* SECTION: Subscription — компактна кнопка веде на окрему сторінку */}
      <SubscriptionRow />

      {/* SECTION: Bio — collapsible (юзер 2026-05-24: «згорни блоки») */}
      <div className="px-5 mt-3">
        <ProfileAccordion
          title="Про тебе"
          description="Опис для AI — використовується для генерації відгуків на вакансії"
          icon="help"
          open={bioSectionOpen}
          onToggle={() => setBioSectionOpen((v) => !v)}
          locked={!profileIsPaid}
          lockedHint="Доступно з підпискою — AI-відгуки на вакансії генеруються з цього опису."
        >
          <p className="text-[0.8125rem] leading-relaxed text-ink-dim mb-3">
            Розкажи про себе у вільній формі: ким працюєш, скільки років досвіду,
            твої ніші та інструменти, цінова політика, чи готовий до релокейту.
            <b className="text-ink"> AI генеруватиме відгуки на вакансії саме на основі цього тексту.</b>
          </p>
          <div className="relative">
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={maxChars}
              rows={9}
              placeholder="Привіт! Я motion-designer з 5+ роками досвіду. Працюю в After Effects, Cinema 4D. Робив проекти для брендів X, Y, Z. Ставка: $35/год. Готовий до B2B і прямих клієнтів. Перевага — англомовні замовлення..."
              className="w-full bg-bg rounded-xl p-3 pr-11 text-[0.875rem] leading-relaxed text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
            {/* Кошик — очистити опис (юзер 2026-06-14). Тап → стирає
                візуально, автосейв прибирає й у базі. */}
            {bio.trim().length > 0 && (
              <button
                type="button"
                aria-label="Очистити"
                onClick={() => {
                  tg()?.HapticFeedback?.impactOccurred?.("light");
                  setBio("");
                }}
                className="absolute right-2.5 top-2.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-surface shadow-card-soft text-primary active:scale-90 transition"
              >
                <Icon name="trash" size={15} />
              </button>
            )}
          </div>
          {/* Голос → текст (юзер 2026-06-14): надиктувати про себе. */}
          <div className="mt-2">
            <VoiceBioButton
              onResult={(t) =>
                setBio((prev) =>
                  prev.trim() ? `${prev.trimEnd()}\n\n${t}` : t,
                )
              }
            />
          </div>
          <div className="mt-2 flex items-center justify-between font-mono text-[0.6875rem]">
            <span
              className={
                bioValid ? "text-ink-dim" : "text-red-500 font-medium"
              }
            >
              {bioLen} / {maxChars}
              {!bioValid && bioLen > 0 && (
                <span className="text-red-500">
                  {" "}· ще {Math.max(0, minChars - bioLen)} до мінімуму
                </span>
              )}
            </span>
            <span className="text-ink-dim">
              мінімум {minChars}, максимум {maxChars} символів
            </span>
          </div>

          {/* Посилання на портфоліо — НЕобов'язкове (owner 2026-09-15): якщо є,
              AI згадає його у відгуку; якщо немає — генерація все одно працює. */}
          <div className="mt-4 rounded-xl bg-bg/60 border border-ink/[0.06] p-3">
            <div className="mb-1.5 flex items-center gap-1.5 text-[0.78125rem] font-semibold text-ink">
              Посилання на портфоліо{" "}
              <span className="font-normal text-ink-faint">— якщо є</span>
            </div>
            <input
              type="url"
              inputMode="url"
              value={portfolioUrl}
              onChange={(e) => setPortfolioUrl(e.target.value)}
              maxLength={500}
              placeholder="https://behance.net/… , t.me/… , Google Drive…"
              className="w-full bg-surface rounded-lg px-3 py-2.5 text-[0.875rem] text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            {portfolioUrl.trim() !== "" && !isLikelyUrl(portfolioUrl) ? (
              <div className="mt-1.5 text-[0.6875rem] leading-relaxed text-red-500 font-medium">
                Схоже, це не посилання. Встав робоче посилання (напр.
                behance.net/…, t.me/…).
              </div>
            ) : (
              <div className="mt-1.5 text-[0.6875rem] leading-relaxed text-ink-dim">
                Обов'язкове: без посилання на портфоліо (і опису «Про тебе») AI не
                згенерує відгук. AI ненав'язливо додасть його у відповідь.
              </div>
            )}
          </div>
        </ProfileAccordion>
      </div>

      {/* SECTION: Сфери — collapsible */}
      <div className="px-5 mt-3">
        <ProfileAccordion
          title="Сфери вакансій"
          description="Категорії послуг які тебе цікавлять"
          icon="flame"
          open={catsSectionOpen}
          onToggle={() => setCatsSectionOpen((v) => !v)}
        >
          <SpheresPicker
            items={categories}
            selected={selectedCats}
            onToggle={toggleCat}
            onSelectAll={() => setSelectedCats(new Set(categories.map((c) => c.id)))}
            onClear={() => setSelectedCats(new Set())}
          />
        </ProfileAccordion>
      </div>

      {/* SECTION: Джерела — collapsible */}
      <div className="px-5 mt-3">
        {(() => {
          return (
            <ProfileAccordion
              title="Джерела вакансій"
              description="Звідки збирати вакансії — Telegram, Threads, Freelancehunt, Finder"
              icon="translate"
              open={sourcesSectionOpen}
              onToggle={() => setSourcesSectionOpen((v) => !v)}
            >
              <div className="rounded-xl bg-bg/40 px-1.5 py-1">
                {SOURCE_META.map((s, i) => {
                  const isLast = i === SOURCE_META.length - 1;
                  const isOn = sources[s.key];
                  // Premium-джерела fhunt/finder — замок для FREE.
                  const locked =
                    (s.key === "fhunt" || s.key === "finder") && !profileIsPaid;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={
                        locked
                          ? () => {
                              tg()?.HapticFeedback?.notificationOccurred?.("warning");
                              showAppAlert(
                                "Оформи підписку, щоб отримувати вакансії з цього джерела.",
                                { title: "Джерело під замком", emoji: "🔒" },
                              );
                            }
                          : () => toggleSource(s.key)
                      }
                      className={
                        "w-full flex items-center gap-3 px-3 py-3 text-left transition-colors rounded-xl " +
                        (!isLast ? "border-b border-ink/[0.06] " : "") +
                        "active:bg-surface-soft"
                      }
                    >
                      <div className="flex-1 min-w-0">
                        <div className={"text-[0.875rem] font-medium " + (locked ? "text-ink-dim" : "text-ink")}>
                          {s.title}
                        </div>
                        <div className="text-[0.75rem] text-ink-dim mt-0.5 break-words">
                          {s.description}
                        </div>
                      </div>
                      {locked ? (
                        <Icon name="lock" size={14} color="#A8A29C" />
                      ) : (
                        <div
                          className={
                            "relative h-7 w-12 shrink-0 rounded-full transition " +
                            (isOn ? "bg-primary" : "bg-ink/15")
                          }
                        >
                          <div
                            className={
                              "absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all " +
                              (isOn ? "left-[1.375rem]" : "left-0.5")
                            }
                          />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </ProfileAccordion>
          );
        })()}
      </div>

      {/* SECTION: Мова вакансій (owner 2026-09-19). Попередження про російську
          обовʼязкове: більшість таких вакансій — від українських замовників,
          і вимкнення ріже приблизно пʼяту частину потоку. */}
      <div className="px-5 mt-3">
        <ProfileAccordion
          title="Мова вакансій"
          /* Рядки опису приблизно однакові (правило текстів): перенос
             ставимо самі, інакше «вакансії» лишалось коротким хвостом. */
          description={
            "Якими мовами отримувати вакансії —\nукраїнська, російська, англійська"
          }
          icon="globe"
          locked={!profileIsPaid}
          lockedHint="Вибір мов доступний з підпискою. На безкоштовному тарифі вакансії приходять усіма мовами."
          open={langSectionOpen}
          onToggle={() => setLangSectionOpen((v) => !v)}
        >
          <div className="rounded-xl bg-bg/40 px-1.5 py-1">
            {LANG_META.map((l, i) => {
              const isLast = i === LANG_META.length - 1;
              const isOn = langs.includes(l.key);
              const onlyOne = isOn && langs.length === 1;
              return (
                <button
                  key={l.key}
                  type="button"
                  disabled={onlyOne}
                  onClick={onlyOne ? undefined : () => toggleLang(l.key)}
                  className={
                    "w-full flex items-center gap-3 px-3 py-3 text-left transition-colors rounded-xl " +
                    (!isLast ? "border-b border-ink/[0.06] " : "") +
                    (onlyOne ? "cursor-default" : "active:bg-surface-soft")
                  }
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[0.875rem] font-medium text-ink">
                      {l.title}
                    </div>
                    <div className="text-[0.75rem] text-ink-dim mt-0.5 break-words">
                      {l.description}
                    </div>
                  </div>
                  <div
                    className={
                      "relative h-7 w-12 shrink-0 rounded-full transition " +
                      (isOn ? "bg-primary" : "bg-ink/15") +
                      (onlyOne ? " opacity-60" : "")
                    }
                  >
                    <div
                      className={
                        "absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all " +
                        (isOn ? "left-[1.375rem]" : "left-0.5")
                      }
                    />
                  </div>
                </button>
              );
            })}
          </div>
          {langs.includes("ru") ? (
            <div className="mt-2 rounded-xl bg-primary/[0.08] px-3 py-2.5 text-[0.75rem] leading-snug text-ink [text-wrap:balance]">
              Перш ніж вимикати російську: майже всі такі вакансії — від
              українських замовників, які просто пишуть російською за звичкою.
              Це приблизно кожна п'ята вакансія, і ти її більше не побачиш.
            </div>
          ) : (
            <div className="mt-2 rounded-xl bg-danger/[0.08] px-3 py-2.5 text-[0.75rem] leading-snug text-ink [text-wrap:balance]">
              Російську вимкнено — ти не бачиш приблизно кожну п'яту вакансію.
              Майже всі вони від українських замовників, які пишуть російською
              за звичкою, а не від росіян.
            </div>
          )}
          <p className="text-[0.71875rem] leading-snug text-ink-dim mt-2 px-1 [text-wrap:balance]">
            Вакансії, де мову визначити не вдалось, приходять завжди — щоб нічого
            не загубилось.
          </p>
        </ProfileAccordion>
      </div>

      {/* SECTION: Вакансії чи замовлення (owner 2026-09-20). Вакансія — коли
          шукає компанія чи команда, замовлення — коли конкретна людина шукає
          виконавця під задачу. Те, що AI не визначила, приходить завжди. */}
      <div className="px-5 mt-3">
        <ProfileAccordion
          title="Вакансії чи замовлення"
          description={
            "Що саме отримувати — роботу в компанії\nчи разові замовлення"
          }
          icon="briefcase"
          /* Owner 2026-09-25: вибір відкрито для підписників (мітка v2).
             Безкоштовним — сірий із замком, як мова вакансій. Бейдж «beta»
             лишається, доки не переконаємось, що мітка працює без скарг. */
          badge="beta"
          locked={!profileIsPaid}
          lockedHint="Вибір «Вакансії чи замовлення» доступний з підпискою. На безкоштовному тарифі приходить і те, і те."
          open={workKindSectionOpen}
          onToggle={() => setWorkKindSectionOpen((v) => !v)}
        >
          <div className="rounded-xl bg-bg/40 px-1.5 py-1">
            {WORK_KIND_META.map((k, i) => {
              const isLast = i === WORK_KIND_META.length - 1;
              const isOn = workKinds.includes(k.key);
              const onlyOne = isOn && workKinds.length === 1;
              return (
                <button
                  key={k.key}
                  type="button"
                  disabled={onlyOne}
                  onClick={onlyOne ? undefined : () => toggleWorkKind(k.key)}
                  className={
                    "w-full flex items-center gap-3 px-3 py-3 text-left transition-colors rounded-xl " +
                    (!isLast ? "border-b border-ink/[0.06] " : "") +
                    (onlyOne ? "cursor-default" : "active:bg-surface-soft")
                  }
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[0.875rem] font-medium text-ink">
                      {k.title}
                    </div>
                    <div className="text-[0.75rem] text-ink-dim mt-0.5 break-words [text-wrap:balance]">
                      {k.description}
                    </div>
                  </div>
                  <div
                    className={
                      "relative h-7 w-12 shrink-0 rounded-full transition " +
                      (isOn ? "bg-primary" : "bg-ink/15") +
                      (onlyOne ? " opacity-60" : "")
                    }
                  >
                    <div
                      className={
                        "absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all " +
                        (isOn ? "left-[1.375rem]" : "left-0.5")
                      }
                    />
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-[0.71875rem] leading-snug text-ink-dim mt-2 px-1 [text-wrap:balance]">
            Пости, де тип не зрозумілий із тексту, приходять завжди — щоб нічого
            не загубилось.
          </p>
        </ProfileAccordion>
      </div>

      {/* SECTION: Формат вакансій — online/offline/hybrid + Не вказано
          (owner 2026-07-23). AI визначає формат кожної вакансії; юзер обирає
          які отримувати. «Не вказано» заблоковано-ВКЛ (багато вакансій без
          явного формату — вимкнути = втратити багато). */}
      <div className="px-5 mt-3">
        <ProfileAccordion
          title="Формат вакансій"
          description={"Які формати роботи\nотримувати — онлайн, офлайн, гібрид"}
          icon="laptop"
          locked={!profileIsPaid}
          lockedHint="Вибір формату роботи доступний з підпискою. На безкоштовному тарифі приходять вакансії всіх форматів."
          open={workFmtSectionOpen}
          onToggle={() => setWorkFmtSectionOpen((v) => !v)}
        >
          <div className="rounded-xl bg-bg/40 px-1.5 py-1">
            {WORK_FORMAT_META.map((f, i) => {
              const isLast = i === WORK_FORMAT_META.length - 1;
              const locked = f.locked === true;
              const isOn = locked
                ? true
                : workFormats.includes(f.key as WorkFormat);
              return (
                <button
                  key={f.key}
                  type="button"
                  disabled={locked}
                  onClick={
                    locked
                      ? undefined
                      : () => toggleWorkFormat(f.key as WorkFormat)
                  }
                  className={
                    "w-full flex items-center gap-3 px-3 py-3 text-left transition-colors rounded-xl " +
                    (!isLast ? "border-b border-ink/[0.06] " : "") +
                    (locked ? "cursor-default" : "active:bg-surface-soft")
                  }
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[0.875rem] font-medium text-ink">
                      {f.title}
                    </div>
                    <div className="text-[0.75rem] text-ink-dim mt-0.5 break-words">
                      {f.description}
                    </div>
                  </div>
                  <div
                    className={
                      "relative h-7 w-12 shrink-0 rounded-full transition " +
                      (isOn ? "bg-primary" : "bg-ink/15") +
                      (locked ? " opacity-60" : "")
                    }
                  >
                    <div
                      className={
                        "absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-all " +
                        (isOn ? "left-[1.375rem]" : "left-0.5")
                      }
                    />
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-[0.71875rem] leading-snug text-ink-dim mt-2 px-1 [text-wrap:balance]">
            «Не вказано» — AI не визначив формат. Приходять завжди, щоб нічого не пропустити.
          </p>
        </ProfileAccordion>
      </div>

      {/* SECTION: Оформлення вакансій — формат бот-сповіщень (юзер 2026-06-12).
          PRO обирає оригінал/summary; FREE завжди отримує summary → замок
          (як premium-джерела fhunt/finder вище). */}
      <div className="px-5 mt-3">
          <ProfileAccordion
            title="Оформлення вакансій"
            description={"Як виглядатимуть сповіщення\nпро вакансії в боті"}
            icon="doc"
            locked={!profileIsPaid}
            lockedHint="Вибір формату доступний з підпискою. На безкоштовному тарифі вакансії приходять коротким переказом."
            open={formatSectionOpen}
            onToggle={() => setFormatSectionOpen((v) => !v)}
          >
            <p className="text-[0.8125rem] leading-relaxed text-ink-dim mb-3 [text-wrap:balance]">
              Обери, у якому вигляді бот надсилатиме нові вакансії — повний
              текст із джерела чи короткий AI-переказ.
            </p>
            <div className="space-y-3">
              {VACANCY_FORMAT_OPTIONS.map((opt) => {
                const selected = vacancyFormat === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setVacancyFormat(opt.value)}
                    className={
                      "block w-full text-left rounded-2xl p-3 transition-all duration-200 " +
                      (selected
                        ? "bg-primary/[0.05] ring-2 ring-primary"
                        : "bg-bg/40 ring-1 ring-ink/[0.07] active:bg-surface-soft")
                    }
                  >
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div
                        className={
                          "grid h-5 w-5 place-items-center rounded-full shrink-0 transition " +
                          (selected ? "bg-primary" : "ring-2 ring-ink/20")
                        }
                      >
                        {selected && (
                          <Icon name="check" size={12} color="#fff" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[0.875rem] font-semibold text-ink">
                          {opt.label}
                        </div>
                        <div className="text-[0.75rem] text-ink-dim leading-snug break-words">
                          {opt.sublabel}
                        </div>
                      </div>
                    </div>
                    {/* Telegram-style міні-ілюстрація повідомлення */}
                    <div className="ml-[1.875rem] rounded-2xl rounded-bl-md bg-surface shadow-card px-3 py-2.5">
                      <div className="whitespace-pre-line text-[0.6875rem] leading-relaxed text-ink [overflow-wrap:anywhere]">
                        {opt.preview}
                      </div>
                      <div className="mt-2 border-t border-ink/[0.06] pt-2 text-[0.625rem] leading-relaxed text-ink-dim">
                        <span className="font-semibold text-ink/70">Джерело:</span>{" "}
                        Telegram-канал
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </ProfileAccordion>
      </div>

      {/* SECTION: Генерація відповідей (owner 2026-09-12: «я б у профілі додав
          розділ генерацію відповідей, щоб усе налаштувати не тільки з кнопки
          згенерувати»). Замок — для безкоштовних; для підписника з порожнім
          профілем тап каже заповнити «Про тебе». */}
      {/* px-5 mt-3 — РІВНО як у сусідніх розділів. Без цього картка була
          ширша за інші й вилазила з ряду (owner 2026-09-12: «навіть кнопку
          рівно зробити не міг»). */}
      <div className="px-5 mt-3">
        <ProfileAccordion
          title="Генерація відповідей"
          description={"Промпти, за якими AI пише\nвідгуки на вакансії"}
          icon="brain"
          locked={!profileIsPaid}
          lockedHint="Генерація відгуків на вакансії доступна з підпискою. Там же налаштовуються промпти — наші й до п'яти своїх."
          open={aiSectionOpen}
          onToggle={() => {
            if (!aiSectionOpen && !aiProfileReady) {
              showAppAlert(
                "Спершу заповни «Про тебе» — без цього відгук згенерувати неможливо.",
                { title: "Заповни профіль", emoji: "📝" },
              );
              return;
            }
            setAiSectionOpen((v) => !v);
          }}
        >
          <AIPresetsManager />
        </ProfileAccordion>
      </div>

      {/* SECTION: Додати свої групи — теж ProfileAccordion (єдиний стиль) */}
      <div className="px-5 mt-3">
        <ProfileAccordion
          title="Додати свої групи"
          description="Telegram-канали або чати які бот моніторитиме персонально"
          icon="plus"
          open={groupsOpen}
          onToggle={() => setGroupsOpen((v) => !v)}
        >
          <p className="text-[0.8125rem] leading-relaxed text-ink-dim mb-3 [text-wrap:balance]">
            Ми моніторимо тисячі каналів — але, можливо, ти знаєш групу, якої в
            нас ще немає. <b className="text-ink">Скинь посилання нижче</b> — і
            ми додамо її до моніторингу.
          </p>
          <textarea
            value={groupsText}
            onChange={(e) => setGroupsText(e.target.value)}
            maxLength={2000}
            rows={5}
            placeholder="Додай сюди посилання на групи та канали які ти хочеш додати&#10;&#10;https://t.me/example_channel&#10;https://t.me/example_chat"
            className="w-full bg-bg rounded-xl p-3 text-[0.875rem] leading-relaxed text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
          />
          <button
            type="button"
            disabled={submitGroupsMutation.isPending || groupsText.trim().length < 3}
            onClick={handleSubmitGroups}
            className={
              "mt-3 block w-full rounded-full py-3.5 font-display text-[0.9375rem] font-bold transition " +
              (submitGroupsMutation.isPending || groupsText.trim().length < 3
                ? "bg-ink/10 text-ink-dim cursor-not-allowed"
                : "bg-primary text-white active:bg-primary-deep")
            }
          >
            {submitGroupsMutation.isPending ? "Надсилаю..." : "Надіслати"}
          </button>
        </ProfileAccordion>
      </div>

      {/* SECTION: Чорний список слів (owner 2026-09-06). Людина пише слово
          або фразу — вакансії з ними до неї не потрапляють. */}
      <div className="px-5 mt-3">
        <div
          className={
            "rounded-ios-card bg-surface p-4 shadow-card-soft" +
            (profileIsPaid ? "" : " active:bg-surface-soft transition")
          }
          onClick={
            profileIsPaid
              ? undefined
              : () => {
                  tg()?.HapticFeedback?.selectionChanged?.();
                  showAppAlert(
                    "Чорний список слів доступний з підпискою. На безкоштовному тарифі вакансії приходять без фільтра.",
                    { title: "Доступно з підпискою", emoji: "🔒" },
                  );
                }
          }
        >
          <div className="flex items-center gap-3">
            <span
              className={
                "grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] " +
                (profileIsPaid
                  ? "bg-primary/10 text-primary"
                  : "bg-ink/[0.06] text-ink-faint")
              }
            >
              <Icon name="filter" size={16} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-display text-[0.9375rem] font-bold text-ink">
                Чорний список слів
              </div>
              <div className="mt-0.5 text-[0.78125rem] text-ink-dim leading-snug [text-wrap:balance]">
                Вакансії з цими словами не приходять
              </div>
            </div>
            {!profileIsPaid && (
              <span className="shrink-0 text-ink-faint">
                <Icon name="lock" size={16} />
              </span>
            )}
          </div>

          {profileIsPaid && (
          <div className="mt-3 flex gap-2">
            <input
              value={wordDraft}
              onChange={(e) => setWordDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addWord();
                }
              }}
              maxLength={60}
              placeholder="Наприклад: крипта"
              className="min-w-0 flex-1 rounded-full bg-surface-soft border border-ink/[0.08] px-4 py-2.5 text-[0.8125rem] text-ink outline-none focus:border-primary/40"
            />
            <button
              type="button"
              onClick={addWord}
              disabled={!wordDraft.trim() || updateMutation.isPending}
              className="shrink-0 rounded-full bg-primary px-4 py-2.5 font-display text-[0.8125rem] font-bold text-white active:bg-primary-deep disabled:opacity-40"
            >
              Додати
            </button>
          </div>
          )}

          {profileIsPaid && <BlacklistWarning />}

          {profileIsPaid && blockedWords.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {blockedWords.map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() =>
                    saveWords(blockedWords.filter((x) => x !== w))
                  }
                  className="inline-flex items-center gap-1.5 rounded-full bg-surface-soft border border-ink/[0.08] px-3 py-1.5 text-[0.78125rem] text-ink-dim active:scale-[0.98]"
                >
                  {w}
                  <span className="text-ink-faint">×</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* SECTION: День народження (owner 2026-07-23) — тап → попап.
          У свій день бот дарує 14 днів за 1 грн. */}
      <div className="px-5 mt-3">
        <button
          type="button"
          onClick={() => {
            if (bdayLocked) {
              showAppAlert(
                "Змінити дату народження можна лише один раз. Якщо в ній помилка — напиши в підтримку.",
                { title: "Дату вже змінено", emoji: "🎂" },
              );
              return;
            }
            setBdayModalOpen(true);
          }}
          className="w-full text-left rounded-ios-card bg-surface p-4 shadow-card-soft transition active:scale-[0.99]"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] bg-primary/10 text-primary">
              <Icon name="gift" size={16} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-display text-[0.9375rem] font-bold text-ink">
                День народження
              </div>
              <div className="mt-0.5 text-[0.78125rem] text-ink-dim leading-snug break-words">
                {birthday
                  ? `${formatBirthday(birthday)} · твій подарунок чекає`
                  : "Вкажи дату — і у свій день на тебе чекатиме подарунок-сюрприз"}
              </div>
            </div>
            {!bdayLocked && (
              <span className="text-primary shrink-0">
                <Icon name={birthday ? "edit" : "arrow-right"} size={16} />
              </span>
            )}
          </div>
        </button>
      </div>

      {/* Кнопку «Зберегти» і підпис-статус прибрано — профіль зберігається
          автоматично, текст «зміни зберігаються автоматично» юзер попросив
          прибрати (2026-06-11). */}

      {bdayModalOpen && (
        <BirthdayModal
          onClose={() => setBdayModalOpen(false)}
          onSave={handleSaveBirthday}
          saving={updateMutation.isPending}
          current={birthday}
        />
      )}
    </div>
  );
}

// ============================================================
// Subscription row — компактна кнопка-link в окремий розділ
// ============================================================

function SubscriptionRow() {
  const subQuery = useMySubscription();
  if (subQuery.isLoading) return null;

  const sub = subQuery.data;
  const activeSub = sub && isActiveSubscription(sub) ? sub : null;
  const isActive = activeSub !== null;
  const isFrozen = activeSub?.status === "frozen";
  const daysLeft = activeSub?.days_left ?? 0;
  const expiringSoon = isActive && !isFrozen && daysLeft <= 7;
  // Залишок у днях + годинах (юзер 2026-06-08).
  const _hoursTotal = activeSub
    ? Math.max(0, Math.floor((new Date(activeSub.ends_at).getTime() - Date.now()) / 3_600_000))
    : 0;
  const _dLeft = Math.floor(_hoursTotal / 24);
  const _hLeft = _hoursTotal % 24;
  const leftLabel = `${_dLeft} дн.${_hLeft > 0 ? ` ${_hLeft} год.` : ""}`;

  return (
    <div className="px-5 mt-3">
      <Link
        to="/subscription"
        className={
          "block rounded-ios-card p-4 transition active:scale-[0.99] " +
          (expiringSoon
            ? "bg-gradient-to-br from-orange-50 to-orange-100/50 border border-orange-200/50 shadow-card-soft"
            : isFrozen
              ? "bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200/50 shadow-card-soft"
              : isActive
                ? "bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200/50 shadow-card-soft"
                : "bg-dark text-white shadow-ai-card")
        }
      >
        <div className="flex items-center gap-3">
          <div
            className={
              "grid h-10 w-10 place-items-center rounded-[0.75rem] shrink-0 " +
              (isActive
                ? expiringSoon
                  ? "bg-orange-500 text-white"
                  : isFrozen
                    ? "bg-blue-500 text-white"
                    : "bg-emerald-500 text-white"
                : "bg-primary text-white")
            }
          >
            <Icon
              name={
                isFrozen
                  ? "clock"
                  : expiringSoon
                    ? "clock"
                    : isActive
                      ? "check"
                      // Власник 11.09: «ракета не нравится, пусть всё, что
                      // связано с подпиской, будет молния».
                      : "bolt"
              }
              size={16}
              color="#FFFFFF"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className={
                "font-display text-[0.875rem] font-bold " +
                (isActive ? "text-ink" : "text-white")
              }
            >
              Управління підпискою
            </div>
            <div
              className={
                "text-[0.75rem] mt-0.5 leading-snug " +
                (isActive ? "text-ink-dim" : "text-white/70")
              }
            >
              {isFrozen
                ? "Заморожено • розморозиться сама"
                : expiringSoon
                  ? `⚠️ Залишилось ${leftLabel} — продовжи`
                  : isActive
                    ? `Активна • залишилось ${leftLabel}`
                    : "Оформити підписку та отримати повний доступ до всього функціоналу"}
            </div>
          </div>
          <Icon
            name="arrow-right"
            size={14}
            color={isActive ? "#FE6B04" : "#FE6B04"}
          />
        </div>
      </Link>
    </div>
  );
}

/**
 * ProfileAccordion — згортуваний блок у premium-стилі, **1:1 з SubscriptionRow**.
 *
 * Юзер 2026-05-24: «виглядав красиво, дорого, премиально. А ти зробив
 * блоки і стало некрасиво. Управління підпискою виглядає по одному, інші
 * блоки по-іншому, додати свої групи по третьому. Нема єдиного стилю».
 *
 * Структура (точно як у SubscriptionRow):
 *   ┌────────────────────────────────────────────────────────┐
 *   │ [icon]   Заголовок                                  →  │
 *   │          Коротке опис що всередині                      │
 *   └────────────────────────────────────────────────────────┘
 *
 * Згорнутий стан показує **назву + 1-рядкове опис** (не статистику).
 * При розгортанні стрілка повертається на 90° (→ стає ↓) + тонкий divider.
 */
/**
 * Попередження в чорному списку слів (owner 2026-09-12: «в ідеалі рядок
 * "прочитай, це важливо" і стрілочка, яка розкриває додаткову інформацію»).
 * Згорнутий стан — один рядок, щоб не тиснути на людину щоразу; розкритий
 * пояснює головну ловушку: слово шукається ПІДСТРОКОЮ.
 */
function BlacklistWarning() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 overflow-hidden rounded-[0.875rem] border border-danger/25 bg-danger/[0.06]">
      <button
        type="button"
        onClick={() => {
          tg()?.HapticFeedback?.selectionChanged?.();
          setOpen((v) => !v);
        }}
        className="flex w-full items-center gap-2.5 p-3 text-left active:bg-danger/[0.09] transition"
      >
        <Icon name="flag" size={16} color="#ff3b30" />
        <span className="min-w-0 flex-1 font-display text-[0.78125rem] font-bold text-ink">
          Прочитай, це важливо
        </span>
        <span
          className={
            "shrink-0 transition-transform duration-200 " +
            (open ? "rotate-180" : "")
          }
        >
          <Icon name="chevron" size={14} color="#ff3b30" />
        </span>
      </button>
      <Collapse open={open}>
        <div className="border-t border-danger/20 px-3 pb-3 pt-2.5 text-[0.71875rem] leading-snug text-ink-dim [text-wrap:pretty]">
          <p>
            Розділ — для досвідчених. Одне невдале слово тихо приберe зі стрічки
            сотні хороших вакансій, і ти навіть не побачиш, що саме втратив.
          </p>
          <p className="mt-2">
            Слово шукається <b className="text-ink">підстрокою</b>: «дизайн»
            приберe і «веб-дизайн», і «дизайнера», і «дизайн упаковки». Тому
            додавай лише те, чого впевнено не хочеш бачити ніколи — наприклад
            «крипта» чи «ставки».
          </p>
        </div>
      </Collapse>
    </div>
  );
}

function ProfileAccordion({
  title,
  description,
  icon,
  open,
  onToggle,
  children,
  locked = false,
  lockedHint = "Доступно з підпискою.",
  lockedTitle = "Доступно з підпискою",
  lockedEmoji = "🔒",
  badge,
}: {
  title: string;
  description: string;
  icon: IconName;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  locked?: boolean;
  lockedHint?: string;
  /** Для розділів, закритих не підпискою, а тим, що функція ще в розробці. */
  lockedTitle?: string;
  lockedEmoji?: string;
  /** Таблетка на куті картки (як «new» у стрічці), напр. «beta» для
   *  функції, яку ще обкатуємо (owner 2026-09-25). Розмітку не міняє. */
  badge?: string;
}) {
  return (
    // Обгортка без overflow-hidden — щоб бейдж міг стирчати з кута, як
    // «new» / «переглянуто» на картці вакансії (owner 2026-09-25: «не внутри
    // карточки, а на ней»). Сама картка всередині — без змін.
    <div className="relative">
      {badge && <CornerBadge>{badge}</CornerBadge>}
    <div className="rounded-ios-card bg-surface shadow-card overflow-hidden">
      <button
        type="button"
        onClick={() => {
          tg()?.HapticFeedback?.selectionChanged?.();
          if (locked) {
            showAppAlert(lockedHint, {
              title: lockedTitle,
              emoji: lockedEmoji,
            });
            return;
          }
          onToggle();
        }}
        className="w-full p-4 active:bg-surface-soft transition"
      >
        <div className="flex items-center gap-3">
          {/* Icon-картка 10x10 з brand-tint — 1:1 з SubscriptionRow */}
          <div
            className={
              "grid h-10 w-10 place-items-center rounded-[0.75rem] shrink-0 " +
              (locked ? "bg-ink/[0.05]" : "bg-primary/10")
            }
          >
            <Icon name={icon} size={16} color={locked ? "#9ca3af" : "#FE6B04"} />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div
              className={
                "font-display text-[0.875rem] font-bold " +
                (locked ? "text-ink-dim" : "text-ink")
              }
            >
              {title}
            </div>
            {/* Юзер 2026-06-06: «опис розділів дуже малий, +20%». 11→13px. */}
            {/* Owner 2026-09-12: у замкнених розділів опис підміняли фразою
                «доступно з підпискою» — людина не бачила, ЧОГО вона
                позбавлена. Опис показуємо завжди, а «з підпискою» лишається
                в попапі. Правило — CLAUDE.md, розділ про замок. */}
            <div className="text-[0.75rem] mt-0.5 leading-snug text-ink-dim break-words whitespace-pre-line">
              {description}
            </div>
          </div>
          {locked ? (
            <Icon name="lock" size={14} color="#A8A29C" />
          ) : (
            /* arrow-right (як у Subscription) → повертається на 90° при open */
            <div
              className={
                "shrink-0 transition-transform duration-200 " +
                (open ? "rotate-90" : "rotate-0")
              }
            >
              <Icon name="arrow-right" size={14} color="#FE6B04" />
            </div>
          )}
        </div>
      </button>
      <Collapse open={open && !locked}>
        <div className="h-px bg-ink/[0.06] mx-4" />
        <div className="p-4">{children}</div>
      </Collapse>
    </div>
    </div>
  );
}
